import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ShopPauseState {
  /** True if the shop should currently block ordering (manual OR schedule). */
  paused: boolean;
  /** Raw manual toggle state. */
  manualPaused: boolean;
  /** Scheduled window (ISO strings) or null. */
  startAt: string | null;
  endAt: string | null;
  /** True if a future scheduled pause is set but not yet active. */
  scheduledUpcoming: boolean;
  message: string;
}

function parseDate(v: string | undefined | null): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Reads shop pause settings (manual toggle, schedule window, message).
 * Effective `paused` is true when manual toggle is on OR `now` is in [start, end].
 * Refetches every 60s.
 */
export function useShopPause() {
  return useQuery<ShopPauseState>({
    queryKey: ['shop-pause-state'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_settings')
        .select('key, value')
        .in('key', [
          'shop_paused',
          'shop_pause_message',
          'shop_pause_start',
          'shop_pause_end',
        ]);
      if (error) throw error;
      const map = new Map((data ?? []).map((r) => [r.key, r.value]));
      const manualPaused = (map.get('shop_paused') ?? 'false') === 'true';
      const startAt = (map.get('shop_pause_start') ?? '').trim() || null;
      const endAt = (map.get('shop_pause_end') ?? '').trim() || null;
      const now = new Date();
      const s = parseDate(startAt);
      const e = parseDate(endAt);
      const inWindow = !!(s && e && now >= s && now <= e);
      const scheduledUpcoming = !!(s && e && now < s);
      return {
        paused: manualPaused || inWindow,
        manualPaused,
        startAt,
        endAt,
        scheduledUpcoming,
        message: map.get('shop_pause_message') ?? '',
      };
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}
